import { useMemo } from "react";
import { bool, func, node, string } from "prop-types";
import useFeature from "./useFeature";
import { FeatureProvider } from "./featureContext";
import UpgradeModal from "./UpgradeModal";
import UpgradeGate from "./UpgradeGate";
import useOrganisation from "../../hooks/useOrganisation";
import useModuleInfo from "../../hooks/useModuleInfo";
import useModal from "../../hooks/useModal";
import { DemoConnectModal } from "./DemoGate";
import AddAddonModal from "../Addon/AddAddonModal";
import { featureToAddonId, Modules } from "../../util/constants";
import style from "./FeatureGate.module.scss";

const FeatureGate = ({
  name,
  children,
  renderGate,
  loadingOverlay = false,
  upgradeType,
  upgradeText,
  onSuccess = () => {},
  featureVariant,
  forceUpgradeModal = false,
  moduleName = Modules.RECEIVABLES,
}) => {
  const feature = useFeature(name, featureVariant, moduleName);
  const { sandbox } = useOrganisation();
  const { subscription, moduleSubscriptions = [] } = useModuleInfo(moduleName, {
    suspense: false,
  });

  const hasActiveAdditionalModuleSubscription = moduleSubscriptions.some(
    (moduleSubscription) =>
      moduleSubscription?.id !== Modules.ORGANISATION &&
      moduleSubscription?.isActive,
  );
  // A missing organisation module is created by the API when AddAddonModal fetches the addon.
  const isSubscribed =
    moduleName === Modules.ORGANISATION
      ? (!subscription || Boolean(subscription.isActive)) &&
        hasActiveAdditionalModuleSubscription
      : subscription?.isActive || false;
  const showModal = useModal({ cleanupOnUnmount: false });

  const ctx = useMemo(
    () => ({
      ...feature,
      hasAddons: feature.hasAddons || false,
      sandbox,
      onUpgrade: () => {
        if (sandbox) {
          showModal(<DemoConnectModal />);
        } else if (isSubscribed && feature.hasAddons && !forceUpgradeModal) {
          showModal(
            <AddAddonModal
              feature={feature}
              moduleName={moduleName}
              addonId={featureToAddonId[feature.name]}
              onSuccess={onSuccess}
            />,
          );
        } else {
          showModal(<UpgradeModal moduleName={moduleName} />);
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      feature.enabled,
      feature.canAccess,
      sandbox,
      isSubscribed,
      feature.hasAddons,
      showModal,
      onSuccess,
      feature.name,
    ],
  );

  if (feature.loading) {
    if (!loadingOverlay) return null;
    return (
      <div className={style.loading} aria-busy="true">
        <div className={style.loadingContent}>{children}</div>
        <div className={style.loadingOverlay} />
      </div>
    );
  }
  if (!feature.enabled) return null;

  return (
    <FeatureProvider value={ctx}>
      <UpgradeGate
        text={upgradeText}
        type={upgradeType}
        renderGate={renderGate}
      >
        {children || null}
      </UpgradeGate>
    </FeatureProvider>
  );
};

FeatureGate.propTypes = {
  name: string.isRequired,
  children: node.isRequired,
  renderGate: func,
  loadingOverlay: bool,
  upgradeType: string,
  upgradeText: string,
  forceUpgradeModal: bool,
};

export default FeatureGate;
